import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { Auth, getAuth } from 'firebase/auth';
import { Firestore, getFirestore } from 'firebase/firestore';
import { FirebaseStorage, getStorage } from 'firebase/storage';

// Firebase web SDK config for the parrot-fe170 "Parrot Admin" web app.
//
// Two values must be supplied via env vars — REACT_APP_FIREBASE_API_KEY
// and REACT_APP_FIREBASE_APP_ID. The rest have safe non-secret defaults
// since they're stable per Firebase project.
//
// Production:  set both in Netlify → Site settings → Environment variables.
//              They're public (every browser receives them in the JS bundle),
//              but Netlify's secrets scanner will block deploys if they
//              appear hardcoded in source, so we read from env only.
// Local dev:   copy `.env.example` → `.env.local` and fill in both values.
//
// Heads-up: the iOS Bundle ID and the Web App use different API keys;
// do NOT reuse the iOS GoogleService-Info plist apiKey for the web app
// — it will be rejected by Firestore.

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'parrot-fe170.firebaseapp.com',
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL || 'https://parrot-fe170.firebaseio.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'parrot-fe170',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'parrot-fe170.appspot.com',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '1010612869053',
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-0W33R04VKB',
};

export const isFirebaseConfigured: boolean = !!(config.apiKey && config.appId);

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;

if (isFirebaseConfigured) {
  try {
    _app = getApps().length ? getApps()[0] : initializeApp(config as Required<typeof config>);
    _auth = getAuth(_app);
    _db = getFirestore(_app);
    _storage = getStorage(_app);
  } catch (e) {
    // Keep the marketing routes alive even if Firebase config is
    // malformed. AdminGate surfaces this state to the admin.
    // eslint-disable-next-line no-console
    console.error('[Parrot-web] Firebase init threw — admin routes will be disabled', e);
    _app = null;
    _auth = null;
    _db = null;
    _storage = null;
  }
} else if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn(
    '[Parrot-web] Firebase env vars missing. Admin routes will show a setup banner. ' +
      'Create .env.local with REACT_APP_FIREBASE_API_KEY and REACT_APP_FIREBASE_APP_ID.'
  );
}

export const firebaseApp = _app;
export const auth = _auth;
export const db = _db;
export const storage = _storage;
